/**
 * Shadow Contact popup — wizard-clicker style mid-game ad choice prompts
 * A mysterious informant appears at random intervals offering deals
 */

import type { GameState, GameAction } from '../game/index.js';
import { getTotalCps } from '../game/index.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { AdManager, getAdRewards } from '../monetization/index.js';

type Dispatch = (action: GameAction) => void;
type GetState = () => GameState;

// Config
const CONTACT_INTERVAL_MIN = 180_000; // 3 min
const CONTACT_INTERVAL_MAX = 360_000; // 6 min
const CONTACT_POPUP_DURATION = 30_000; // 30s to decide
const AD_COOLDOWN = 30_000; // 30s between ads

interface ContactOffer {
  id: string;
  titleKey: string;
  descKey: string;
  icon: string;
  apply: (dispatch: Dispatch, getState: GetState) => void;
}

let overlayEl: HTMLDivElement | null = null;
let timerHandle: ReturnType<typeof setInterval> | null = null;
let checkHandle: ReturnType<typeof setInterval> | null = null;
let dispatchRef: Dispatch | null = null;
let getStateRef: GetState | null = null;
let nextContactAt = 0;
let currentOfferExpiresAt = 0;
let lastAdTime = 0;

function getOffers(): ContactOffer[] {
  return [
    {
      id: 'intel_report',
      titleKey: 'sc_offer_intel',
      descKey: 'sc_offer_intel_desc',
      icon: '\u{1F4CA}',
      apply: (dispatch, getState) => {
        const cps = getTotalCps(getState());
        const gain = Math.floor(cps * 1800); // 30 min income
        dispatch({ type: 'ADD_IP', payload: { amount: gain } });
      },
    },
    {
      id: 'exp_serum',
      titleKey: 'sc_offer_serum',
      descKey: 'sc_offer_serum_desc',
      icon: '\u{1F9EA}',
      apply: (dispatch) => {
        dispatch({
          type: 'ACTIVATE_BOOST',
          payload: { boost: { type: 'tap10x', multiplier: 10, endsAt: Date.now() + 30_000 } },
        });
      },
    },
    {
      id: 'black_market',
      titleKey: 'sc_offer_tech',
      descKey: 'sc_offer_tech_desc',
      icon: '\u{1F4BB}',
      apply: (dispatch) => {
        dispatch({
          type: 'ACTIVATE_BOOST',
          payload: { boost: { type: 'cps5x', multiplier: 10, endsAt: Date.now() + 30_000 } },
        });
      },
    },
    {
      id: 'classified_cache',
      titleKey: 'sc_offer_docs',
      descKey: 'sc_offer_docs_desc',
      icon: '\u{1F4DC}',
      apply: (dispatch) => {
        dispatch({ type: 'ADD_DOCS', payload: { amount: 3 } });
      },
    },
  ];
}

function canShowAd(): boolean {
  return Date.now() - lastAdTime >= AD_COOLDOWN;
}

function getAdCooldownSec(): number {
  return Math.max(0, Math.ceil((AD_COOLDOWN - (Date.now() - lastAdTime)) / 1000));
}

function scheduleNext(): void {
  const delay = CONTACT_INTERVAL_MIN + Math.random() * (CONTACT_INTERVAL_MAX - CONTACT_INTERVAL_MIN);
  nextContactAt = Date.now() + delay;
}

export function initShadowContact(dispatch: Dispatch, getState: GetState): void {
  dispatchRef = dispatch;
  getStateRef = getState;
  scheduleNext();

  if (checkHandle) clearInterval(checkHandle);
  checkHandle = setInterval(() => {
    if (overlayEl) return; // already showing
    if (Date.now() < nextContactAt) return;
    showContactPopup();
  }, 5000);
}

function showContactPopup(): void {
  if (overlayEl) hideContactPopup();
  if (!dispatchRef || !getStateRef) return;

  const offers = getOffers();
  const offer = offers[Math.floor(Math.random() * offers.length)];
  currentOfferExpiresAt = Date.now() + CONTACT_POPUP_DURATION;

  // Build description with actual values
  let desc = t(offer.descKey);
  if (offer.id === 'intel_report') {
    const cps = getTotalCps(getStateRef());
    const gain = Math.floor(cps * 1800);
    desc = desc.replace('{amount}', formatNumber(gain));
  }

  overlayEl = document.createElement('div');
  overlayEl.className = 'shadow-contact-overlay';

  const cooldownText = canShowAd() ? '' : ` (${getAdCooldownSec()}s)`;

  overlayEl.innerHTML = `
    <div class="shadow-contact-card">
      <div class="sc-icon">${offer.icon}</div>
      <div class="sc-title">${t('sc_title')}</div>
      <div class="sc-offer-name">${t(offer.titleKey)}</div>
      <div class="sc-desc">${desc}</div>
      <div class="sc-timer" id="sc-timer"></div>
      <div class="sc-actions">
        <button class="sc-btn-accept${canShowAd() ? '' : ' on-cooldown'}" id="sc-accept">
          \u{1F4FA} ${t('sc_accept')}${cooldownText}
        </button>
        <button class="sc-btn-decline" id="sc-decline">${t('sc_decline')}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlayEl);
  playSfx('achievement');

  // Timer update
  const updateTimer = () => {
    const timerEl = document.getElementById('sc-timer');
    if (!timerEl) return;
    const remaining = Math.max(0, Math.ceil((currentOfferExpiresAt - Date.now()) / 1000));
    timerEl.textContent = `${remaining}s`;
    if (remaining <= 0) {
      hideContactPopup();
      scheduleNext();
    }

    // Update cooldown on button
    const btn = document.getElementById('sc-accept');
    if (btn) {
      if (canShowAd()) {
        btn.classList.remove('on-cooldown');
        btn.innerHTML = `\u{1F4FA} ${t('sc_accept')}`;
      } else {
        btn.classList.add('on-cooldown');
        btn.innerHTML = `\u{1F4FA} ${t('sc_accept')} (${getAdCooldownSec()}s)`;
      }
    }
  };
  updateTimer();
  timerHandle = setInterval(updateTimer, 1000);

  // Accept button
  document.getElementById('sc-accept')?.addEventListener('click', () => {
    if (!canShowAd()) return;
    if (!dispatchRef || !getStateRef) return;
    const dispatch = dispatchRef;
    const getState = getStateRef;
    const rewards = getAdRewards();

    AdManager.showRewardedAd(
      rewards.INCOME_X5,
      () => {
        lastAdTime = Date.now();
        offer.apply(dispatch, getState);
        dispatch({ type: 'AD_WATCHED', payload: { rewardType: `shadow_${offer.id}` } });
        playSfx('boost');
        hideContactPopup();
        scheduleNext();
      },
      () => {
        hideContactPopup();
        scheduleNext();
      },
    );
  });

  // Decline button
  document.getElementById('sc-decline')?.addEventListener('click', () => {
    hideContactPopup();
    scheduleNext();
  });

  // Background click dismisses
  overlayEl.addEventListener('click', (e) => {
    if (e.target === overlayEl) {
      hideContactPopup();
      scheduleNext();
    }
  });
}

function hideContactPopup(): void {
  if (timerHandle) { clearInterval(timerHandle); timerHandle = null; }
  if (overlayEl) { overlayEl.remove(); overlayEl = null; }
}

export function cleanupShadowContact(): void {
  hideContactPopup();
  if (checkHandle) { clearInterval(checkHandle); checkHandle = null; }
}
